import { HTMLAttributes, useState } from "react"
import * as Icons from "@nova/component-icons"
import { WorkspaceHomeCTA } from "../WorkspaceHomeCTA"
import { ModalNames } from "features/modals/types"
import { WorkspaceHomeSectionTitle } from "../WorkspaceHomeSectionTitle"
import classNames from "classnames"
import { Project } from "features/data-management/workspace/types"
import { useWorkspacePermissions } from "features/data-management/workspace/useWorkspacePermissions"
import {
    useActivateProjectWithImmediateModal,
    useCreateProject,
} from "features/data-management/workspace/context/navigation"

const LOADING_TEXT = "Creating your first project"

export const WorkspaceHomeCTAsSection = (rest: HTMLAttributes<HTMLDivElement>) => {
    const { isEditor } = useWorkspacePermissions()
    const createProject = useCreateProject()
    const [loadingKeys, setLoadingKeys] = useState(new Set<string>())

    const createSampleProject = (
        key: string,
        name: string,
        callback: (project: Project) => Promise<void>
    ) => {
        setLoadingKeys((prev) => new Set([...prev, key]))
        createProject({
            preventSwitchingOfWorkspaceMode: true,
            name,
            callback: async (project) => {
                await callback(project)
                setLoadingKeys((prev) => {
                    prev.delete(key)
                    return new Set(prev)
                })
            },
        })
    }

    const activateOrthoProject = useActivateProjectWithImmediateModal(
        ModalNames.WelcomeTryOrthomosaicsModal
    )
    const activateWelcomeProject = useActivateProjectWithImmediateModal(
        ModalNames.TryOurAddOnsModal
    )

    if (!isEditor) return null

    return (
        <div {...rest} className={classNames("mt-8", rest.className)}>
            <WorkspaceHomeSectionTitle title="Start your first project" shouldShowBorder={false} />
            <div className="flex gap-6">
                <WorkspaceHomeCTA
                    Icon={<Icons.Icon Icon={Icons.Map} className="text-lazeryellow" />}
                    title="Create your first project"
                    onClick={() => createProject()}
                    disabled={loadingKeys.size > 0}
                    loadingText={LOADING_TEXT}
                />
                <WorkspaceHomeCTA
                    Icon={
                        <Icons.Icon
                            Icon={Icons.Ortho}
                            className="text-gray-0"
                            accentColor="#F1FF66" // lazeryellow
                        />
                    }
                    title="Create your first Orthomosaic"
                    onClick={() =>
                        createSampleProject("first_ortho", "My First Orthomosaic", (project) =>
                            activateOrthoProject(project.id)
                        )
                    }
                    isLoading={loadingKeys.has("first_ortho")}
                    disabled={loadingKeys.size > 0}
                    loadingText={LOADING_TEXT}
                />
                <WorkspaceHomeCTA
                    Icon={<Icons.Icon Icon={Icons.Lightbulb} className="text-lazeryellow" />}
                    title="Try our Features & Tools"
                    onClick={() =>
                        createSampleProject("first_tools", "My First Project", (project) =>
                            activateWelcomeProject(project.id)
                        )
                    }
                    isLoading={loadingKeys.has("first_tools")}
                    disabled={loadingKeys.size > 0}
                    loadingText={LOADING_TEXT}
                />
            </div>
        </div>
    )
}
